import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import Table from 'react-bootstrap/Table';
import FormControl from 'react-bootstrap/FormControl';
import Button from 'react-bootstrap/Button';

import CustomPagination from '../../CustomPagination';
import Loader from '../../../components/Common/Loader';
import Link from '../../Link/Link';

import messages from '../../../locale/messages';
import debounce from '../../../helpers/debounce';
import { deleteLocation } from '../../../actions/siteadmin/Location/deleteLocation';
import { updateLocation } from '../../../actions/siteadmin/Location/updateLocation';
import getCurrentPage from '../../../helpers/getCurrentPage';
import { demoRestriction } from '../../../actions/demoRestriction';

import { CommonAdminEditIcon, CommonAdminTrashIcon } from '../../Common/Assets/SVGIcon/CommonSvgIcon';

import bt from '../../../components/commonStyle.css';
import s from './ManageLocation.css';

const LocationList = ({ locationList, deleteLocation, updateLocation, intl: { formatMessage }, demoRestriction }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchList, setSearchList] = useState('');

  const handleKeywordSearch = debounce((search) => {
    const { refetch } = locationList;
    setSearchList(search);
    setCurrentPage(1);
    refetch({ currentPage: 1, searchList: search });
  });

  const handleDelete = async (id) => {
    const { refetch, getLocationList } = locationList;
    const adjustedPage = await getCurrentPage({ results: getLocationList?.results, page: currentPage });
    const isRestrict = await demoRestriction({ restrictType: 'location', restrictValue: id });
    if (isRestrict) return;
    const response = await deleteLocation(id, adjustedPage);
    if (response?.status === 200) {
      setCurrentPage(adjustedPage);
      refetch({ currentPage: adjustedPage });
    }
  };

  const handleChange = async (e, location) => {
    const { refetch } = locationList;
    const isActive = e.target.value;
    const isRestrict = await demoRestriction({ restrictType: 'location', restrictValue: location.id });
    if (isRestrict) return;
    await updateLocation(location.locationName, location.coordinates, location.id, location.description, isActive);
    refetch({ currentPage: 1 });
    setCurrentPage(1);
  };

  const paginationData = (page) => {
    const { refetch } = locationList;
    setCurrentPage(page);
    refetch({ currentPage: page });
  };

  const { getLocationList, loading } = locationList;

  return (
    <div className="whiteDropdown">
      <div className={s.exportDisplay}>
        <div className={s.searchGroupContainer}>
          <div className={cx(s.searchInput, 'searchInputRTL')}>
            <FormControl
              type="text"
              placeholder={formatMessage(messages.searchOnly)}
              onChange={(e) => handleKeywordSearch(e.target.value)}
              className={bt.formControlInput}
            />
          </div>
        </div>
        <div className={cx(s.exportTextSection)}>
          <Link to="/siteadmin/add-location" className={cx(bt.btnPrimary, bt.displayInlineBlock)}>
            {formatMessage(messages.addLocation)}
          </Link>
        </div>
      </div>

      {loading ? (
        <Loader type="circle" />
      ) : (
        <div className='tableOuterContainer'>
          <Table className="tableContainer">
            <thead>
              <tr>
                <th><FormattedMessage {...messages.id} /></th>
                <th><FormattedMessage {...messages.location} /></th>
                <th><FormattedMessage {...messages.description} /></th>
                <th><FormattedMessage {...messages.status} /></th>
                <th><FormattedMessage {...messages.action} /></th>
                <th><FormattedMessage {...messages.deleteAction} /></th>
              </tr>
            </thead>
            <tbody>
              {getLocationList?.results?.length > 0 ? (
                getLocationList.results.map((item, index) => {
                  const isActive = item?.isActive ? 1 : 0;
                  return (
                    <tr key={index}>
                      <td data-label={formatMessage(messages.id)}>{item.id}</td>
                      <td data-label={formatMessage(messages.location)}>{item.locationName}</td>
                      <td data-label={formatMessage(messages.description)}>{item.description}</td>
                      <td data-label={formatMessage(messages.status)}>
                        <div>
                          <select
                            name="isActive"
                            onChange={(e) => handleChange(e, item)}
                            className={bt.selectInput}
                            value={isActive}
                          >
                            <option value={1}>{formatMessage(messages.active)}</option>
                            <option value={0}>{formatMessage(messages.inactive)}</option>
                          </select>
                        </div>
                      </td>
                      <td data-label={formatMessage(messages.action)}>
                        <div>
                          <Link to={`/siteadmin/edit-location/${item.id}`} className={s.noLink}>
                            <CommonAdminEditIcon className={cx(s.editIcon, 'editIconRTL')} />
                            <span className={s.vtrMiddle}>
                              <FormattedMessage {...messages.editAction} />
                            </span>
                          </Link>
                        </div>
                      </td>
                      <td data-label={formatMessage(messages.deleteAction)}>
                        <div>
                          <Button onClick={() => handleDelete(item.id)} className={s.iconBtn}>
                            <CommonAdminTrashIcon className={cx(s.editIcon, 'editIconRTL')} />
                            <span className={s.vtrMiddle}>
                              <FormattedMessage {...messages.deleteAction} />
                            </span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className={'tableNoRecordCel'}><FormattedMessage {...messages.noResult} /></td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {getLocationList?.results?.length > 0 && (
        <div className={cx(bt.space5, bt.spaceTop5)}>
          <CustomPagination
            total={getLocationList.count}
            currentPage={currentPage}
            defaultCurrent={1}
            defaultPageSize={10}
            change={paginationData}
            paginationLabel={getLocationList.count === 1 ? formatMessage(messages.locationName) : formatMessage(messages.locations)}
          />
        </div>
      )}
    </div>
  );
};

LocationList.propTypes = {
  locationList: PropTypes.object,
  deleteLocation: PropTypes.func,
  updateLocation: PropTypes.func,
  intl: PropTypes.object.isRequired
};

const mapDispatch = {
  deleteLocation,
  updateLocation,
  demoRestriction
};

export default connect(null, mapDispatch)(injectIntl(withStyles(bt, s)(LocationList)));
